/**
 * Vitrine de produtos do perfil.
 *
 * So existe quando o estabelecimento tem produto a venda: sem estoque, nao ha
 * secao — uma vitrine vazia diria que a loja nao tem nada, o que e diferente
 * de nao ter loja.
 *
 * Mostra nome, foto e preco. A quantidade em estoque fica de fora: e numero de
 * operacao, e publicar isso na pagina de vendas nunca foi decisao de ninguem.
 *
 * Cada card leva ao wizard, onde a etapa de produtos faz o pedido junto do
 * agendamento — o mesmo destino das outras vitrines (DEC-2 da spec).
 */

import Link from "next/link";
import { FiImage, FiShoppingBag } from "react-icons/fi";
import type { PublicProduct } from "../../../services/public";
import { resolveImageUrl } from "../utils";
import { formatBRL } from "../../../lib/currency";
import { PreviewCarousel } from "./PreviewCarousel";

interface ProfileProductsPreviewProps {
  products: PublicProduct[];
  bookingHref: string;
}

export function ProfileProductsPreview({
  products,
  bookingHref,
}: ProfileProductsPreviewProps) {
  const disponiveis = products.filter((product) => product.available);
  if (disponiveis.length === 0) return null;

  return (
    <section
      // OFR-21: o link de vitrine do lembrete abre o perfil nesta seção.
      id="produtos"
      aria-label="Produtos do estabelecimento"
      data-testid="profile-products-preview"
      className="rounded-2xl bg-white px-3 py-4 shadow-sm"
    >
      <div className="mb-1 px-1">
        <h2 className="text-base font-bold text-[#233055]">Leve para casa</h2>
        <p className="mt-0.5 text-sm text-slate-600">
          Produtos que você pode pedir junto com o agendamento.
        </p>
      </div>

      <PreviewCarousel
        items={disponiveis}
        getKey={(product) => product.id}
        renderItem={(product) => {
          const precoLabel = formatBRL(Number(product.salePriceCents || 0) / 100);
          const imageSrc = product.imageUrl ? resolveImageUrl(product.imageUrl) : "";
          const marca = String(product.brand || "").trim();

          return (
            <Link
              href={bookingHref}
              data-testid={`profile-product-card-${product.id}`}
              aria-label={`${product.name}${marca ? `, ${marca}` : ""}, ${precoLabel}. Toque para começar o agendamento e pedir junto.`}
              className="flex h-full flex-col overflow-hidden rounded-2xl border border-slate-200 bg-white transition hover:border-primary-300 focus:outline-none focus-visible:ring-4 focus-visible:ring-primary-100"
            >
              <div className="relative aspect-[4/3] w-full overflow-hidden bg-slate-100">
                {imageSrc ? (
                  <>
                    {/* Fundo desfocado preenche a moldura sem recortar a foto. */}
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageSrc}
                      alt=""
                      aria-hidden="true"
                      className="absolute inset-0 h-full w-full scale-125 object-cover opacity-45 blur-2xl"
                    />
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={imageSrc}
                      alt=""
                      className="relative z-10 h-full w-full object-contain"
                    />
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center text-slate-400">
                    <FiImage className="h-8 w-8" aria-hidden="true" />
                  </div>
                )}
              </div>

              <div className="p-3" aria-hidden="true">
                <p className="line-clamp-2 text-base font-semibold leading-tight text-[#233055]">
                  {product.name}
                </p>
                <div className="mt-1.5 flex items-center justify-between gap-2 text-sm text-slate-600">
                  {/* PUB-15: em 390px a marca encolhe com reticências; o preço fica inteiro. */}
                  <span className="inline-flex min-w-0 flex-1 items-center gap-1.5">
                    <FiShoppingBag className="h-4 w-4 shrink-0" aria-hidden="true" />
                    <span className="truncate">{marca || product.unitLabel}</span>
                  </span>
                  <span className="shrink-0 whitespace-nowrap font-semibold text-primary-700">
                    {precoLabel}
                  </span>
                </div>
              </div>
            </Link>
          );
        }}
        ariaLabel="Produtos em destaque"
        previousLabel="Ver produto anterior"
        nextLabel="Ver próximo produto"
        testId="profile-products-carousel"
      />
    </section>
  );
}
